import useLocalStorage from "@rooks/use-localstorage";
import cx from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import ReactResizeDetector from "react-resize-detector";
import { Button, DropdownItem, DropdownMenu, DropdownToggle, Form, FormGroup, Input, Label, UncontrolledDropdown } from "reactstrap";

import { StateCallbacks } from "../state";
import { extractKeysAndIndexes } from "../state/getDefaultState";
import { DataPointValue, NonGroupTaxonomyEntry, Settings, State } from "../types";
import { Pages } from "./Pages";
import { References } from "./References";
import { Text } from "./Text";
import { onSelectText as onSelectTextCallback, TextSelection } from "./TextSelection";

type Props = Readonly<{
  disableEditing: boolean;
  renderPages: boolean;
  review: boolean;
  settings: Settings;
  state: State;
  stateCallbacks: StateCallbacks;
  canSubmit: boolean;
  onReset: () => void;
  onClear: () => void;
  disabled: boolean;
}>;

type ResizeDetectorCallbackArgs = Readonly<{
  height: number | undefined;
  width: number | undefined;
}>;

type UIState = Readonly<{
  view: "images" | "text";
  zoom: string;
}>;
const DEFAULT_UI_STATE = JSON.stringify({
  automaticJump: false,
  view: "images",
  zoom: "1",
});
const DEFAULT_NON_GRAPHICAL_UI_STATE: UIState = {
  view: "text",
  zoom: "1",
};

export const Document = ({
  disableEditing,
  renderPages,
  review,
  settings,
  state,
  stateCallbacks,
  onReset,
  onClear,
  disabled,
}: Props) => {
  const [persistedUIState, updatePersistedUIState] = useLocalStorage(
    "MAPPING-UI-STATE",
    DEFAULT_UI_STATE,
  );
  const defaultState = useMemo(() => {
    const parsedDefault = JSON.parse(DEFAULT_UI_STATE);
    try {
      const persisted = JSON.parse(persistedUIState);
      if (typeof persisted !== "object" || persisted === null || Array.isArray(persisted)) {
        return parsedDefault;
      }
      return { ...parsedDefault, ...persisted };
    } catch (err) {
      console.error(err);
      return parsedDefault;
    }
  }, [persistedUIState]);
  const [uiState, updateUIState] = useState<UIState>(
    renderPages ? defaultState : DEFAULT_NON_GRAPHICAL_UI_STATE,
  );
  const { view, zoom } = uiState;
  const [showReferences, setShowReferences] = useState<string>("");
  const onSelectBlock = useCallback(
    (id: string, text: string, replaceValue: boolean) => {
      if (review) {
        return;
      }
      stateCallbacks.onSelectBlock(id, text, replaceValue);
    },
    [review, stateCallbacks.onSelectBlock],
  );
  const onSelectPageBlock = useCallback(
    (id: string, text: string, replaceValue: boolean) => {
      onSelectBlock(id, text, replaceValue);
      if (settings.automaticallyJumpToText) {
        updateUIState(s => {
          const newState: UIState = { ...s, view: "text" };
          updatePersistedUIState(JSON.stringify(newState));
          return newState;
        });
        setShowReferences("");
      }
    },
    [settings.automaticallyJumpToText, onSelectBlock, setShowReferences],
  );
  let activeDataPoint: DataPointValue<any> | null = null;
  let activeTaxonomyEntry: NonGroupTaxonomyEntry | null = null;
  if (state.values[state.activeValue]) {
    activeDataPoint = state.values[state.activeValue];
    activeTaxonomyEntry = extractKeysAndIndexes(activeDataPoint.valueKey, state.taxonomy.taxonomyObject).taxonomyEntry as NonGroupTaxonomyEntry;
  }
  const onSelectText = onSelectTextCallback(
    activeTaxonomyEntry,
    activeDataPoint,
    stateCallbacks.onUpdateValue,
  );
  const [confirmTableSelection, updateConfirmTableSelection] = useState(true);
  const disableTableSelectionConfirmation = useCallback(() => {
    updateConfirmTableSelection(false);
  }, [updateConfirmTableSelection]);
  const onCloseReferences = useCallback(() => {
    setShowReferences("");
  }, [setShowReferences]);
  const onClickReference = useCallback(
    (refId: string) => {
      setShowReferences("");
      stateCallbacks.onSelectDataPoint(refId);
    },
    [setShowReferences, stateCallbacks.onSelectDataPoint],
  );
  return (
    <div id="documentContainer" className="d-flex flex-column overflow-hidden">
        <div className="align-items-center border-bottom border-right d-flex flex-row justify-content-between p-2">
          <div>
            <Button
              color="primary"
              outline={view !== "images"}
              disabled={!renderPages}
              onClick={() => {
                updateUIState(s => {
                  const newState: UIState = { ...s, view: "images" };
                  updatePersistedUIState(JSON.stringify(newState));
                  setShowReferences("");
                  return newState;
                });
              }}
              style={{ width: "80px" }}
            >
              Pages
            </Button>
            {' '}
            <Button
              color="primary"
              onClick={() => {
                updateUIState(s => {
                  const newState: UIState = { ...s, view: "text" };
                  updatePersistedUIState(JSON.stringify(newState));
                  setShowReferences("");
                  return newState;
                });
              }}
              outline={view !== "text"}
              style={{ width: "80px" }}
            >
              Text
            </Button>
          </div>
          <Form inline>
            <FormGroup>
              {view === "images" &&
                <Label htmlFor="zoom">
                  Zoom:
                  <Input
                    className={cx("ml-1", { "mr-3": !review })}
                    disabled={view !== "images"}
                    id="zoom"
                    onChange={e => {
                      const {
                        target: { value },
                      } = e;
                      updateUIState(s => {
                        const newState: UIState = { ...s, zoom: value };
                        updatePersistedUIState(JSON.stringify(newState));
                        return newState;
                      });
                    }}
                    type="select"
                    value={zoom}
                  >
                    <option value="1">Fit Width</option>
                    <option value="1.5">150%</option>
                    <option value="2">200%</option>
                    <option value="2.5">250%</option>
                  </Input>
                </Label>
              }
              <UncontrolledDropdown>
                <DropdownToggle caret color="secondary">
                  Reset
                </DropdownToggle>
                <DropdownMenu right>
                  <DropdownItem disabled={disabled} onClick={onReset}>
                    &hellip; to predictions
                  </DropdownItem>
                  <DropdownItem disabled={disabled} onClick={onClear}>
                    &hellip; to an empty document
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </FormGroup>
          </Form>
        </div>
      <div className="flex-fill position-relative">
        <ReactResizeDetector handleHeight handleWidth>
          {({ height, width }: ResizeDetectorCallbackArgs) => {
            if (height === undefined || width === undefined) {
              return <div />;
            }
            const viewHeight = Math.floor(height);
            const viewWidth = Math.floor(width);
            return (
              <TextSelection onSelectText={onSelectText}>
                {(renderPages  && uiState.view === "images") && (
                  <div
                  id="mappingPages"
                    className="bg-secondary position-absolute"
                    style={{
                      height: `${viewHeight}px`,
                      left: view === "images" ? "0" : `-${viewWidth + 5}px`,
                      top: "0",
                      transition: "left ease-in-out 0.5s",
                      width: "100%",
                    }}
                  >
                    <Pages
                      confirmTableSelection={confirmTableSelection}
                      disableTableSelectionConfirmation={disableTableSelectionConfirmation}
                      onSelectBlock={onSelectPageBlock}
                      onAddCorrections={stateCallbacks.onAddCorrections}
                      setShowReferences={setShowReferences}
                      state={state}
                      viewWidth={viewWidth}
                      zoom={parseFloat(zoom)}
                    />
                  </div>
                )}
                {uiState.view === "text" && (<div
                  id="mappingText"
                  className="bg-white position-absolute"
                  style={{
                    bottom: "0",
                    left: view === "text" ? "0" : `${viewWidth + 5}px`,
                    top: "0",
                    transition: "left ease-in-out 0.5s",
                    width: "100%",
                  }}
                >
                  <Text
                    confidenceThreshold={state.confidenceThreshold}
                    confirmTableSelection={confirmTableSelection}
                    disableEditing={disableEditing || review}
                    disableTableSelectionConfirmation={disableTableSelectionConfirmation}
                    onAddCorrections={stateCallbacks.onAddCorrections}
                    onSelectBlock={onSelectBlock}
                    setShowReferences={setShowReferences}
                    state={state}
                  />
                </div>)
                }
              </TextSelection>
            );
          }}
        </ReactResizeDetector>
        {!!showReferences && (
          <References
            block={state.blocks[showReferences]}
            confidenceThreshold={state.confidenceThreshold}
            key={showReferences}
            onClickReference={onClickReference}
            onCloseReferences={onCloseReferences}
            values={state.values}
            zoneId={view === "images" ? showReferences : `link-${showReferences}`}
          />
        )}
      </div>
    </div>
  );
};
Document.displayName = "Document";
